import { Component, createRef, Fragment } from 'preact';
import { motion } from 'framer-motion';
import { Mail, Users, Sparkles } from 'lucide-react';
import AppHeader from './AppHeader';
import { screenPanel } from '../utils/screenEnter';
import AmbientParticles from './AmbientParticles';
import StoreTorchFX from './StoreTorchFX';
import CandleFlames from './CandleFlames';

const AUCTION_DEFAULT_CANDLES = [
  { left: '41.8%', top: '32.4%', size: 11, glow: 70, speed: 3.2, phase: 5.116844769990166 },
  { left: '36.6%', top: '28.7%', size: 11, glow: 70, speed: 3.2, phase: 0.9861807933462932 },
  { left: '32.9%', top: '47.2%', size: 11, glow: 70, speed: 3.2, phase: 1.3282141755040595 },
  { left: '33.9%', top: '47.5%', size: 11, glow: 70, speed: 3.2, phase: 4.355026797278501 },
  { left: '5.3%', top: '62.2%', size: 11, glow: 70, speed: 3.2, phase: 3.477981565239489 },
  { left: '7.2%', top: '22.7%', size: 11, glow: 70, speed: 3.2, phase: 2.823892885594754 },
  { left: '21.5%', top: '47.9%', size: 11, glow: 70, speed: 3.2, phase: 0.9846696908079513 },
  { left: '20.6%', top: '50.0%', size: 11, glow: 70, speed: 3.2, phase: 3.4758984711382217 },
];

import DeckCardTile from './DeckCardTile';
import CardGrid from './CardGrid';
import CardInspector from './CardInspector';
import { InspectorHoldTracker } from '../utils/inspectorHold';
import { pushDismiss, removeDismiss } from '../utils/escStack';
import CardFilterBar from './CardFilterBar';
import CoverArtCanvas from './CoverArtCanvas';
import CandlePlacementDev from './CandlePlacementDev';
import { isDevBuild } from '../utils/devMode';
import RuneSpinner from './RuneSpinner';
import { CoinIcon } from './ui/icons';
import { BackButton, CloseButton } from './ui/navButtons';
import { isFoilFinish, FOIL_LABEL, resolvePrinting } from '../utils/sorcery/foil.js';
import { foilZonesForCard } from '../utils/sorcery/foilZones';
import {
  ART_GROUP_BASE, artGroupsForCard, printingsInArtGroup, artGroupLabel, artGroupDisplayName,
} from '../utils/sorcery/artGroups';
import { DEFAULT_FOIL_PARAMS } from '../utils/sorcery/foilParams';
import FoilPreview from './FoilPreview';
import { playUI, UI } from '../utils/arena/uiSounds';
import { cn } from '../lib/utils';
import { k, t } from '../localization/t';
import {
  fetchListings,
  fetchMyListings,
  createListing,
  buyListing,
  cancelListing,
} from '../utils/arena/auctionApi';
import { loadArenaProfile } from '../utils/arena/profileApi';
import {
  buildOwnedMap, buildVariantOwnedMap, getOwnedQty,
} from '../utils/arena/collectionUtils';
import {
  BG_ATMOSPHERE, VIGNETTE, GOLD, GOLD_TEXT, TEXT_PRIMARY, TEXT_BODY, TEXT_MUTED,
  PANEL_BG, PANEL_BORDER, BEVELED_BTN, GOLD_BTN, DANGER_BTN, INPUT_STYLE,
  TAB_ACTIVE_TEXT, TAB_INACTIVE, TAB_BAR_STYLE, COIN_COLOR, ACCENT_GOLD,
  DIALOG_STYLE, FourCorners, OrnamentalDivider, SECTION_HEADER_STYLE,
} from '../lib/medievalTheme';
import { TabBar } from './ui/TabBar';

const AUCTION_TABS = [
  { key: 'browse', label: k('Browse') },
  { key: 'sell', label: k('Sell & My Listings') },
];
import VikingOrnament from './VikingOrnament';
import TutorialOverlay from './TutorialOverlay';
import { shouldAutoPlay, markTutorialSeen, hydrateTutorialState } from '../utils/arena/tutorialState';

const AUCTION_TUTORIAL_KEY = 'auction-house';

function auctionTutorialSteps() {
  return [
  {
    key: 'welcome',
    title: t('Welcome to the Auction House'),
    body: t('The auction house is the player-driven marketplace. Browse listings put up by other players and buy cards with gold, or list your own duplicates for sale. Everything is peer-to-peer; the game takes a small cut on each sale.'),
  },
  {
    key: 'browse-tab',
    title: t('Browse'),
    body: t('Start on Browse to see every active listing. Use the filters to narrow by element, rarity, or name, and sort by price or newest. Click any listing to preview the card, then Buy to claim it; the card arrives in your mailbox moments later.'),
    selector: '[data-tutorial="auction-tab-browse"]',
  },
  {
    key: 'listings-grid',
    title: t('Listings Grid'),
    body: t("Every listing shows the card, rarity, foiling, asking price, and the seller's name. A gold glow means you're looking at a foil or rainbow foil. Clicking a listing opens a detail view with a bigger preview."),
    selector: '[data-tutorial="auction-content"]',
  },
  {
    key: 'sell-tab',
    title: t('Sell & My Listings'),
    body: t("Switch to Sell to put your own cards up for auction and to manage listings you've already posted. You can cancel an active listing at any time; the card comes back through your mailbox, same as a purchase."),
    selector: '[data-tutorial="auction-tab-sell"]',
  },
  {
    key: 'sell-picker',
    title: t('Pick a Card to List'),
    body: t("Your sellable cards appear on the left. Cards already in a saved deck are hidden automatically so you never accidentally list something you're playing with. Pick a card, set a price, and hit List to post it for sale."),
  },
  {
    key: 'mail-delivery',
    title: t('Mail Delivery'),
    body: t('Every auction transfer happens through the mailbox: sold cards arrive as mail attachments, so nothing lives in limbo. Same for cancelled listings: the card is returned to you via mail and you claim it like any other delivery. Check your mailbox regularly.'),
  },
  ];
}

function cardImageUrl(card, printingId) {
  if (printingId && card?.printings) {
    const match = card.printings.find((p) => p.unique_id === printingId);
    if (match) return match.image_url || '';
  }
  return card?.printings?.[0]?.image_url || '';
}

function findCard(sorceryCards, cardId) {
  return (sorceryCards || []).find((c) => c.unique_id === cardId);
}

// Flat seller cut on every sale. Mirrors the server's rate; there are
// no tiers, so the number is a constant on both sides.
export const LISTING_FEE_PERCENT = 15;

// Mirrors the server's computeAuctionFee. The fee comes out of the
// seller's payout, never the buyer's price. A 1-coin sale is exempt
// because coins are indivisible and a fee would round the seller's
// net to zero. Returns { tax, proceeds }.
export function computeAuctionFee(salePrice, feePercent) {
  const price = Math.max(0, Math.floor(salePrice || 0));
  if (price <= 1) return { tax: 0, proceeds: price };
  const tax = Math.floor(price * (feePercent || 0) / 100);
  return { tax, proceeds: price - tax };
}

// One-off easter egg: flag a specific million-gold Witch listing
// by user "triumph" with a tongue-in-cheek badge.
function isScroogeListing(listing, card) {
  if (!listing) return false;
  const seller = (listing.sellerName || '').toLowerCase();
  const name = (card?.name || listing.cardName || '').toLowerCase();
  return seller === 'triumph' && name === 'witch' && listing.price === 1000000;
}

function sortIndicator(activeKey, currentKey, order) {
  if (activeKey !== currentKey) return '';
  return order === 'asc' ? ' \u2191' : ' \u2193';
}

function SorceryElementIcon({ element, className = 'size-3.5' }) {
  const triangles = {
    Water: { points: '6,11 1,2 11,2', line: null, color: '#01FFFF' },
    Earth: { points: '6,11 1,2 11,2', line: [2.5, 5, 9.5, 5], color: '#CFA572' },
    Fire: { points: '6,1 11,10 1,10', line: null, color: '#FF5F00' },
    Air: { points: '6,1 11,10 1,10', line: [2.5, 7, 9.5, 7], color: '#A0BADB' },
  };
  const t = triangles[element];
  if (!t) return null;
  return (
    <svg viewBox="0 0 12 12" className={className} fill="none" stroke={t.color} strokeWidth="1.5" strokeLinejoin="round">
      <polygon points={t.points} />
      {t.line ? <line x1={t.line[0]} y1={t.line[1]} x2={t.line[2]} y2={t.line[3]} /> : null}
    </svg>
  );
}

const AUCTION_DURATION_MS = 30 * 24 * 60 * 60 * 1000; // 30 days

function formatTimeRemaining(createdAt, now) {
  if (!createdAt) return null;
  const expires = new Date(createdAt).getTime() + AUCTION_DURATION_MS;
  const remaining = expires - now;
  if (remaining <= 0) return t('Expired');
  const days = Math.floor(remaining / (24 * 60 * 60 * 1000));
  const hours = Math.floor((remaining % (24 * 60 * 60 * 1000)) / (60 * 60 * 1000));
  const mins = Math.floor((remaining % (60 * 60 * 1000)) / (60 * 1000));
  const secs = Math.floor((remaining % (60 * 1000)) / 1000);
  if (days > 0) return `${days}d ${hours}h ${mins}m`;
  if (hours > 0) return `${hours}h ${mins}m ${secs}s`;
  return `${mins}m ${secs}s`;
}

const RARITY_COLORS = {
  Ordinary: TEXT_MUTED,
  Exceptional: '#6ea8d4',
  Elite: '#b480d4',
  Unique: ACCENT_GOLD,
  Avatar: '#c45050',
};

const CARD_TILE_STYLE = {
  background: PANEL_BG,
  border: `1px solid ${GOLD} 0.12)`,
  borderRadius: 'var(--radius-lg)',
};

const SIDEBAR_PANEL = {
  background: PANEL_BG,
};

export default class AuctionHouse extends Component {
  constructor(props) {
    super(props);
    this.state = {
      tab: props.initialTab || 'browse',
      focusedListingId: null,
      listings: [],
      listingsTotal: 0,
      listingsLoading: false,
      sortBy: 'date',
      sortOrder: 'desc',
      page: 0,
      candles: AUCTION_DEFAULT_CANDLES,
      selectedCardId: null,
      selectedArtGroup: ART_GROUP_BASE,
      selectedFoiling: 'S',
      sellPrice: '',
      sellQuantity: 1,
      sellLoading: false,
      sellError: null,
      myListings: [],
      myListingsLoading: false,
      error: null,
      syncing: false,
      buyingId: null,
      cancellingId: null,
      previewListing: null,
      // Browse tab filter predicate + raw text (text is sent to the
      // listings API; everything else filters client-side).
      browseSearchQuery: '',
      browseMatchesCard: () => true,
      browseMatchesFoiling: () => true,
      sellMatchesCard: () => true,
      sellMatchesFoiling: () => true,
      now: Date.now(),
      hoveredCard: null,
      inspectedEntry: null,
      showAuctionTutorial: false,
    };
    this.browseGridRef = createRef();
    this.sellGridRef = createRef();
    this.canvasRef = createRef();
    this.inspectorHold = new InspectorHoldTracker();
  }

  componentDidMount() {
    this._tickTimer = setInterval(() => this.setState({ now: Date.now() }), 1000);
    this.doSync();
    this.loadListings();
    if (this.props.focusListingId) this.focusListing(this.props.focusListingId);
    else if (this.state.tab === 'sell') this.loadMyListings();
    document.addEventListener('keydown', this.handleInspectorKeyDown);
    document.addEventListener('keyup', this.handleInspectorKeyUp);

    // First-run onboarding, deferred so the listings grid paints
    // before the overlay measures its targets. Waits on hydration
    // so the on-disk seen flag is read first.
    const profileId = this.props.profile?.id;
    if (profileId) {
      hydrateTutorialState().then(() => {
        if (this._unmounted) return;
        if (!shouldAutoPlay(profileId, AUCTION_TUTORIAL_KEY)) return;
        this._tutorialTimer = setTimeout(() => {
          if (!this._unmounted) this.setState({ showAuctionTutorial: true });
        }, 600);
      });
    }
  }

  componentDidUpdate(prevProps, prevState) {
    // Deep-links (notification center) can land while already mounted.
    if (this.props.initialTab && this.props.initialTab !== prevProps.initialTab
        && this.props.initialTab !== this.state.tab) {
      this.switchTab(this.props.initialTab);
    }
    if (this.props.focusListingId && this.props.focusListingId !== prevProps.focusListingId) {
      this.focusListing(this.props.focusListingId);
    }
    if (this.state.focusedListingId && this.state.focusedListingId !== prevState.focusedListingId) {
      this.scrollFocusedListingIntoView();
    }

    // Space-was-armed → hover landed on a card → open inspector now.
    if (
      !this.state.inspectedEntry &&
      this.state.hoveredCard &&
      this.state.hoveredCard !== prevState.hoveredCard &&
      this.inspectorHold.consumePending()
    ) {
      // eslint-disable-next-line react/no-did-update-set-state
      this.setState({ inspectedEntry: this.state.hoveredCard });
    }

    const inspectorOpen = this.state.inspectedEntry != null;
    const wasInspectorOpen = prevState.inspectedEntry != null;
    if (inspectorOpen && !wasInspectorOpen) {
      this._escDismissId = pushDismiss(() => this.setState({ inspectedEntry: null }));
    } else if (!inspectorOpen && wasInspectorOpen) {
      removeDismiss(this._escDismissId);
    }

    // After listings grow (append landed), keep filling if the viewport still
    // isn't satisfied (heavy client filter let few rows through).
    if (prevState.listings !== this.state.listings) this.maybeLoadMore();
  }

  componentWillUnmount() {
    this._unmounted = true;
    if (this._tutorialTimer) clearTimeout(this._tutorialTimer);
    clearInterval(this._tickTimer);
    clearTimeout(this._purchaseMsgTimer);
    clearTimeout(this._focusTimer);
    document.removeEventListener('keydown', this.handleInspectorKeyDown);
    document.removeEventListener('keyup', this.handleInspectorKeyUp);
    removeDismiss(this._escDismissId);
  }

  handleAuctionTutorialDismiss = () => {
    const profileId = this.props.profile?.id;
    if (profileId) markTutorialSeen(profileId, AUCTION_TUTORIAL_KEY);
    this.setState({ showAuctionTutorial: false });
  };

  handleInspectorKeyDown = (e) => {
    if ((e.key === ' ' || e.code === 'Space') && !e.target?.isContentEditable && !['INPUT', 'TEXTAREA', 'SELECT'].includes(e.target?.tagName)) {
      e.preventDefault();
      // OS key-repeat fires while Space is held. Only act on the
      // first keydown so hold-to-inspect stays open until keyup.
      if (e.repeat) return;
      if (this.state.inspectedEntry) {
        this.inspectorHold.noteClose();
        this.setState({ inspectedEntry: null });
      } else if (this.state.hoveredCard) {
        this.inspectorHold.noteOpen();
        this.setState({ inspectedEntry: this.state.hoveredCard });
      } else {
        // No card hovered yet. Arm so the next hover opens.
        this.inspectorHold.noteArmed();
      }
    }
  };

  // Releasing Space closes the inspector only if it was held past the
  // hold threshold; quick taps leave it open (tap-toggle mode).
  handleInspectorKeyUp = (e) => {
    if (e.key !== ' ' && e.code !== 'Space') return;
    // Tracker first: releasing Space also disarms a hover-less press.
    if (!this.inspectorHold.shouldCloseOnKeyUp() || !this.state.inspectedEntry) return;
    e.preventDefault();
    this.setState({ inspectedEntry: null });
  };

  doSync = async () => {
    const { profile } = this.props;
    if (!profile.serverToken) return;
    this.setState({ syncing: true });
    try {
      const fresh = await loadArenaProfile(profile.serverToken);
      if (fresh && fresh.coins !== profile.coins) {
        this.props.onUpdateProfile({ ...profile, coins: fresh.coins });
      }
    } catch {
    } finally {
      this.setState({ syncing: false });
    }
  };

  // Infinite scroll: `append` fetches the next page from where the loaded set
  // ends and concatenates it; a fresh load (filter/sort/search change) replaces
  // the set and snaps the grid back to the top. The _loadingMore flag serializes
  // appends so a burst of scroll events can't double-fetch the same offset.
  loadListings = async (append = false) => {
    if (append) {
      if (this._loadingMore || this._noMoreListings) return;
      this._loadingMore = true;
    } else {
      // Fresh load (filter/sort/search). A new result set may have more pages.
      this._noMoreListings = false;
    }
    this.setState({ listingsLoading: !append, error: append ? this.state.error : null });
    try {
      const { browseSearchQuery, sortBy, sortOrder } = this.state;
      const offset = append ? this.state.listings.length : 0;
      const result = await fetchListings({
        search: browseSearchQuery || undefined,
        sortBy,
        sortOrder,
        limit: 50,
        offset,
      });
      const more = result.listings || [];
      // A short page is the last page. The authoritative end signal (the
      // server `total` can be stale and is NOT trusted to terminate).
      this._noMoreListings = more.length < 50;
      this.setState((s) => ({
        // Empty append keeps the SAME array ref so componentDidUpdate doesn't
        // re-trigger maybeLoadMore into a loop.
        listings: append ? (more.length ? [...s.listings, ...more] : s.listings) : more,
        listingsTotal: result.total ?? s.listingsTotal,
      }));
      if (!append) this.browseGridRef.current?.resetScroll();
    } catch (err) {
      this.setState({ error: err.message });
    } finally {
      if (append) this._loadingMore = false;
      this.setState({ listingsLoading: false });
    }
  };

  // Trigger an append when the user nears the bottom of the VISIBLE (client-
  // filtered) list and the server still has more. Called from the grid's
  // onViewChange (on row crossings) and from componentDidUpdate so a page that
  // added few post-filter rows keeps pulling until the viewport is satisfied.
  maybeLoadMore = () => {
    const v = this._lastView;
    if (!v) return;
    if (this._loadingMore || this._noMoreListings) return;
    if (v.topIndex + v.cols * 8 >= (this._filteredCount || 0)) this.loadListings(true);
  };

  handleBrowseFilterChange = ({ state, matchesCard, matchesFoiling }) => {
    // Server-side pagination keys off of the raw text portion; the rest
    // filters client-side. Debounced so rapid typing doesn't spam the API.
    const textChanged = state.searchQuery !== this.state.browseSearchQuery;
    this.setState({
      browseMatchesCard: matchesCard,
      browseMatchesFoiling: matchesFoiling,
      browseSearchQuery: state.searchQuery,
      page: textChanged ? 0 : this.state.page,
    }, () => {
      if (textChanged) {
        clearTimeout(this._searchTimer);
        this._searchTimer = setTimeout(this.loadListings, 300);
      }
    });
  };

  handleSellFilterChange = ({ matchesCard, matchesFoiling }) => {
    this.setState({
      sellMatchesCard: matchesCard,
      sellMatchesFoiling: matchesFoiling,
    });
    this.sellGridRef.current?.resetScroll();
  };

  handleSort = (sortBy) => {
    this.setState(
      (s) => ({
        sortBy,
        sortOrder: s.sortBy === sortBy && s.sortOrder === 'asc' ? 'desc' : 'asc',
        page: 0,
      }),
      this.loadListings,
    );
  };

  handleBuy = async (listing) => {
    const { profile } = this.props;
    const salePrice = listing.price;
    if (!salePrice || salePrice > profile.coins) {
      playUI(UI.ERROR);
      this.setState({ error: t('Not enough gold') });
      return;
    }
    this.setState({ buyingId: listing.id, error: null });
    try {
      await buyListing(profile.serverToken, listing.id);
      playUI(UI.PURCHASE);

      // Reload profile to pick up the new coin balance
      const fresh = await loadArenaProfile(profile.serverToken).catch(() => null);
      if (fresh) {
        this.props.onUpdateProfile({
          ...profile,
          coins: fresh.coins ?? profile.coins,
          collection: fresh.collection ?? profile.collection,
        });
      }

      this.setState((s) => ({
        listings: s.listings.filter((l) => l.id !== listing.id),
        listingsTotal: s.listingsTotal - 1,
        previewListing: s.previewListing?.id === listing.id ? null : s.previewListing,
        purchaseMessage: t('{name} purchased! Check your mailbox to collect it.', { name: listing.cardName || t('Card') }),
      }));
      clearTimeout(this._purchaseMsgTimer);
      this._purchaseMsgTimer = setTimeout(() => this.setState({ purchaseMessage: null }), 5000);
    } catch (err) {
      this.setState({ error: err.message });
    } finally {
      this.setState({ buyingId: null });
    }
  };

  handleCreateListing = async () => {
    const { profile, sorceryCards } = this.props;
    const { selectedCardId, sellPrice, sellQuantity } = this.state;
    const price = parseInt(sellPrice, 10);
    const qty = Math.max(1, parseInt(sellQuantity, 10) || 1);
    if (!selectedCardId || !price || price <= 0) return;

    const card = findCard(sorceryCards, selectedCardId);
    if (!card) return;

    this.setState({ sellLoading: true, sellError: null });
    try {
      const foiling = this.state.selectedFoiling || 'S';
      const artGroup = this.state.selectedArtGroup || ART_GROUP_BASE;

      for (let i = 0; i < qty; i++) {
        await createListing(profile.serverToken, selectedCardId, card.name, price, artGroup, foiling);
      }
      playUI(UI.GOLD);

      // Reload profile (server has already deducted the card from the collection)
      const fresh = await loadArenaProfile(profile.serverToken).catch(() => null);
      if (fresh) {
        this.props.onUpdateProfile({
          ...profile,
          coins: fresh.coins ?? profile.coins,
          collection: fresh.collection ?? profile.collection,
        });
      }

      this.setState({
        selectedCardId: null,
        selectedArtGroup: ART_GROUP_BASE,
        selectedFoiling: 'S',
        sellPrice: '',
        sellQuantity: 1,
      });
      this.loadMyListings();
      // Refresh the parent's saved decks so deck card counts reflect reality
      if (this.props.onRefreshDecks) this.props.onRefreshDecks();
    } catch (err) {
      this.setState({ sellError: err.message });
    } finally {
      this.setState({ sellLoading: false });
    }
  };

  loadMyListings = async () => {
    this.setState({ myListingsLoading: true, error: null });
    try {
      const listingsResult = await fetchMyListings();
      this.setState({
        myListings: Array.isArray(listingsResult) ? listingsResult : (listingsResult?.listings || []),
      });
    } catch (err) {
      this.setState({ error: err.message });
    } finally {
      this.setState({ myListingsLoading: false });
    }
  };

  handleCancel = async (listing) => {
    const { profile } = this.props;
    this.setState({ cancellingId: listing.id, error: null });
    try {
      await cancelListing(profile.serverToken, listing.id);
      // Card is returned via mail from the server
      this.setState((s) => ({
        myListings: s.myListings.map((l) =>
          l.id === listing.id ? { ...l, status: 'cancelled' } : l,
        ),
        purchaseMessage: t('{name} listing cancelled. Check your mailbox to collect it.', { name: listing.cardName || t('Card') }),
      }));
      clearTimeout(this._purchaseMsgTimer);
      this._purchaseMsgTimer = setTimeout(() => this.setState({ purchaseMessage: null }), 5000);
    } catch (err) {
      this.setState({ error: err.message });
    } finally {
      this.setState({ cancellingId: null });
    }
  };

  // Deep-link to one of the seller's own listings: open the Sell tab,
  // wait for the listings to load, then scroll + briefly highlight the
  // row. A listing id that isn't in the result is ignored silently.
  focusListing = async (listingId) => {
    this.setState({ tab: 'sell', error: null });
    await this.loadMyListings();
    if (this._unmounted) return;
    if (!(this.state.myListings || []).some((l) => l.id === listingId)) return;
    clearTimeout(this._focusTimer);
    this.setState({ focusedListingId: listingId });
    this._focusTimer = setTimeout(() => {
      if (!this._unmounted) this.setState({ focusedListingId: null });
    }, 3000);
  };

  scrollFocusedListingIntoView() {
    const id = this.state.focusedListingId;
    const el = id && this.base?.querySelector?.(`[data-listing-id="${CSS.escape(String(id))}"]`);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }

  listingRowFocusStyle(listingId) {
    const focused = this.state.focusedListingId === listingId;
    return {
      transition: 'background 600ms ease, box-shadow 600ms ease',
      ...(focused ? { background: 'rgba(212,168,67,0.12)', boxShadow: `inset 3px 0 0 ${ACCENT_GOLD}` } : null),
    };
  }

  switchTab = (tab) => {
    this.setState({ tab, error: null });
    if (tab === 'browse') this.loadListings();
    if (tab === 'sell') this.loadMyListings();
  };

  // --- Browse grid CardGrid callbacks -------------------------------------
  // Resolve the printing a listing refers to. Listings carry no printing id.
  // they store the collection identity, (card, art group, foiling). So the
  // art group is what picks the illustration. Without it an alt-art listing
  // would browse under the booster art and the buyer would be shopping for
  // the wrong card. Returns { card, printing, artGroup, foiling }.
  _resolveListing(listing) {
    const card = findCard(this.props.sorceryCards, listing.cardId);
    const artGroup = listing.artGroup || ART_GROUP_BASE;
    const foiling = listing.foiling || 'S';
    const printing = printingsInArtGroup(card, artGroup, foiling)[0]
      || resolvePrinting(card, null, foiling);
    return { card, printing, artGroup, foiling: foiling || printing?.foiling || 'S' };
  }

  browseGetItemKey = (listing) => listing.id;

  browseGetItemFoil = (listing) => {
    const { card, printing, foiling } = this._resolveListing(listing);
    if (!isFoilFinish(foiling)) return null;
    return { imageUrl: (printing && printing.image_url) || '', zones: foilZonesForCard(card) };
  };

  browseRenderTile = (listing, { imageHidden, onTilt }) => {
    const { card, printing, foiling } = this._resolveListing(listing);
    const isSelected = this.state.previewListing?.id === listing.id;
    return (
      <div
        className="cursor-pointer"
        onClick={() => this.setState({ previewListing: listing })}
      >
        {card && printing ? (
          <DeckCardTile
            entry={{ card, printing: { ...printing, foiling }, zone: 'spellbook', entryIndex: 0 }}
            isSelected={isSelected}
            imageHidden={imageHidden}
            onTilt={onTilt}
            onClick={() => this.setState({ previewListing: listing })}
            onHoverChange={(hovered) => this.setState({ hoveredCard: hovered ? { card, printing: { ...printing, foiling } } : null })}
          />
        ) : (
          <div
            className="w-full rounded-card aspect-[63/88] flex items-center justify-center text-[0.625cqw]"
            style={{ background: `${GOLD} 0.04)`, color: TEXT_MUTED, border: `1px solid ${GOLD} 0.12)` }}
          >
            {listing.cardId}
          </div>
        )}
      </div>
    );
  };

  browseRenderFooter = (listing) => {
    const { card, artGroup, foiling } = this._resolveListing(listing);
    const foil = isFoilFinish(foiling);
    const groupLabel = artGroupLabel(artGroup);
    return (
      <>
        {isScroogeListing(listing, card) ? (
          <div className="flex items-center justify-center mt-1">
            <span
              className="inline-flex items-center gap-1 text-[0.5625cqw] font-semibold px-1.5 py-0.5 rounded whitespace-nowrap"
              style={{
                color: ACCENT_GOLD,
                background: '#0c0a08',
                border: `1px solid ${ACCENT_GOLD}`,
                boxShadow: '0 2px 6px rgba(0,0,0,0.6)',
              }}
            >
              <img src="/scrooge-mcduck.png" alt="" className="inline-block" style={{ height: '1cqw', width: 'auto' }} />
              {t('Scrooge McDuck Approved')}
            </span>
          </div>
        ) : null}
        <div className="text-xs font-semibold truncate mt-1 text-center" style={{ color: TEXT_PRIMARY }}>
          {card ? artGroupDisplayName(card, artGroup) : listing.cardId}
          {foil && <span className="ml-1 text-[0.5625cqw]" style={{ color: foiling === 'R' ? '#c480e0' : '#6ec8d4' }}>{FOIL_LABEL[foiling]}</span>}
          {groupLabel && <span className="ml-1 text-[0.5625cqw]" style={{ color: ACCENT_GOLD }}>{groupLabel}</span>}
        </div>
        <div className="flex items-center justify-center gap-1 mt-0.5">
          <CoinIcon size={11} />
          <span className="text-xs font-bold" style={{ color: COIN_COLOR }}>
            {listing.price}
          </span>
          <span className="text-[0.5625cqw]" style={{ color: TEXT_MUTED }}>
            {t('gold')}
          </span>
        </div>
        {listing.createdAt ? (
          <div className="text-center mt-0.5">
            <span className="text-[0.5625cqw] tabular-nums" style={{ color: TEXT_MUTED }}>
              {formatTimeRemaining(listing.createdAt, this.state.now)}
            </span>
          </div>
        ) : null}
      </>
    );
  };

  // --- Sell grid CardGrid callbacks ---------------------------------------
  sellGetItemKey = (entry) => `${entry.card.unique_id}-${entry.artGroup}-${entry.foiling}`;

  sellGetItemFoil = (entry) => {
    if (!isFoilFinish(entry.foiling)) return null;
    return { imageUrl: (entry.printing && entry.printing.image_url) || '', zones: foilZonesForCard(entry.card) };
  };

  sellIsSelected(entry) {
    return this.state.selectedCardId === entry.card.unique_id
      && (this.state.selectedArtGroup || ART_GROUP_BASE) === entry.artGroup
      && (this.state.selectedFoiling || 'S') === entry.foiling;
  }

  sellGetItemCellStyle = (entry) => (this.sellIsSelected(entry) ? { zIndex: 10 } : {});

  sellRenderTile = (entry, { index, imageHidden, onTilt }) => {
    const { card, printing, artGroup, foiling } = entry;
    const isSelected = this.sellIsSelected(entry);
    return (
      <DeckCardTile
        entry={{ card, printing: { ...printing, foiling }, zone: 'spellbook', entryIndex: index }}
        isSelected={isSelected}
        imageHidden={imageHidden}
        onTilt={onTilt}
        onClick={() => this.setState({ selectedCardId: card.unique_id, selectedArtGroup: artGroup, selectedFoiling: foiling, sellPrice: '', sellQuantity: 1, sellError: null })}
        onHoverChange={(hovered) => this.setState({ hoveredCard: hovered ? { card, printing: { ...printing, foiling } } : null })}
      />
    );
  };

  sellRenderFooter = (entry) => {
    const { card, artGroup, foiling, qty } = entry;
    const foil = isFoilFinish(foiling);
    const groupLabel = artGroupLabel(artGroup);
    return (
      <>
        <div className="flex items-center justify-center gap-1 mt-1">
          {qty > 1 && (
            <span className="text-[0.625cqw] font-bold px-1.5 py-0.5 rounded-full" style={{ background: PANEL_BG, color: ACCENT_GOLD, border: `1px solid ${GOLD} 0.3)` }}>
              ×{qty}
            </span>
          )}
          {foil && <span className="text-[0.5625cqw] font-semibold" style={{ color: foiling === 'R' ? '#c480e0' : '#6ec8d4' }}>{FOIL_LABEL[foiling]}</span>}
          {groupLabel && <span className="text-[0.5625cqw] font-semibold" style={{ color: ACCENT_GOLD }}>{groupLabel}</span>}
        </div>
        <div className="text-[0.625cqw] truncate mt-0.5 text-center px-0.5" style={{ color: TEXT_MUTED }}>{artGroupDisplayName(card, artGroup)}</div>
      </>
    );
  };

  renderBrowse() {
    const { sorceryCards, profile } = this.props;
    const {
      listings, listingsLoading,
      sortBy, sortOrder, previewListing, buyingId,
      browseMatchesCard, browseMatchesFoiling,
    } = this.state;

    const previewCard = previewListing ? findCard(sorceryCards, previewListing.cardId) : null;
    const canAfford = previewListing ? previewListing.price <= profile.coins : false;

    const filteredListings = listings.filter((listing) => {
      const card = findCard(sorceryCards, listing.cardId);
      if (!browseMatchesCard(card)) return false;
      const foiling = listing.foiling
        || card?.printings?.find((p) => p.unique_id === listing.printingId)?.foiling
        || 'S';
      return browseMatchesFoiling(foiling);
    });
    // Stash for maybeLoadMore (compares scroll position against the visible count).
    this._filteredCount = filteredListings.length;

    const sortTabs = (
      <TabBar className="flex items-center" style={TAB_BAR_STYLE} activeIndex={['price', 'date', 'rarity'].indexOf(sortBy)}>
        {[['price', t('Price')], ['date', t('Date')], ['rarity', t('Rarity')]].map(([key, label]) => (
          <button
            key={key}
            data-tab-item
            type="button"
            className="px-3 py-1.5 text-[0.6875cqw] font-medium cursor-pointer capitalize"
            style={sortBy === key ? TAB_ACTIVE_TEXT : TAB_INACTIVE}
            onClick={() => this.handleSort(key)}
          >
            {label}{sortIndicator(sortBy, key, sortOrder)}
          </button>
        ))}
      </TabBar>
    );

    return (
      <div className="flex gap-0 flex-1 min-h-0">
        {/* Left column: Filters + Grid */}
        <div className="flex-[65] flex flex-col min-h-0 pr-4">
          <CardFilterBar
            className="mb-3"
            sorceryCards={sorceryCards}
            features={{ scope: false, finish: true, type: true }}
            onChange={this.handleBrowseFilterChange}
            trailing={sortTabs}
          />

          {/* Listing grid. CardGrid owns its scroll + virtualization and
              renders foil listings through the shared WebGL scene. The loading
              spinner and empty state overlay it; the grid stays mounted so the
              foil context isn't torn down between loads. */}
          <div className="flex-1 flex min-h-0 relative">
            {listingsLoading ? (
              <div className="flex-1 flex flex-col items-center justify-center py-16 gap-3">
                <RuneSpinner size={56} />
                <span className="text-xs" style={{ color: TEXT_MUTED }}>{t('Loading listings...')}</span>
              </div>
            ) : (
              <>
                <CardGrid
                  ref={this.browseGridRef}
                  items={filteredListings}
                  getItemKey={this.browseGetItemKey}
                  getItemFoil={this.browseGetItemFoil}
                  renderTile={this.browseRenderTile}
                  renderFooter={this.browseRenderFooter}
                  onViewChange={(info) => { this._lastView = info; this.maybeLoadMore(); }}
                  measureFooter={(
                    <>
                      <div className="text-xs mt-1" style={{ lineHeight: 1.3 }}>&nbsp;</div>
                      <div className="mt-0.5" style={{ height: '1cqw' }} />
                      <div className="mt-0.5" style={{ height: '1cqw' }} />
                    </>
                  )}
                />
                {filteredListings.length === 0 ? (
                  <div className="absolute inset-0 flex items-center justify-center text-sm" style={{ color: TEXT_MUTED }}>{t('No listings found')}</div>
                ) : null}
              </>
            )}
          </div>

        </div>

        {/* Right column: Card Preview. No scroll, image shrinks to fit */}
        <div className="flex-[35] min-h-0 overflow-hidden" style={SIDEBAR_PANEL}>
          <div className="relative p-4 h-full flex flex-col" style={{ background: PANEL_BG, border: `1px solid ${GOLD} 0.2)`, borderRadius: 'var(--radius-lg)', isolation: 'isolate' }}>
            <FourCorners />
            <VikingOrnament ornament="ringerike004" variant="centerpiece" opacity={0.08} />
            {previewListing && previewCard ? (
              <div className="flex flex-col items-center gap-2 h-full min-h-0">
                <div className="relative shrink min-h-0 rounded-card" style={{ maxWidth: '50%' }}>
                  <img
                    src={cardImageUrl(previewCard, previewListing.printingId)}
                    alt={previewCard.name}
                    className="rounded-card object-cover bg-black/40 w-full h-full"
                    style={{ aspectRatio: '63 / 88' }}
                  />
                  {isFoilFinish(previewListing.foiling || previewCard.printings?.[0]?.foiling || 'S') ? (
                    <FoilPreview
                      imageUrl={cardImageUrl(previewCard, previewListing.printingId)}
                      zones={foilZonesForCard(previewCard)}
                      params={DEFAULT_FOIL_PARAMS}
                    />
                  ) : null}
                  {isScroogeListing(previewListing, previewCard) && (
                    <span
                      className="absolute left-1/2 -translate-x-1/2 bottom-[6%] inline-flex items-center gap-1.5 text-[0.625cqw] font-semibold px-2 py-0.5 rounded pointer-events-none whitespace-nowrap"
                      style={{
                        color: ACCENT_GOLD,
                        background: '#0c0a08',
                        border: `1px solid ${ACCENT_GOLD}`,
                        boxShadow: '0 2px 6px rgba(0,0,0,0.6)',
                      }}
                    >
                      <img
                        src="/scrooge-mcduck.png"
                        alt=""
                        className="inline-block"
                        style={{ height: '1.25cqw', width: 'auto' }}
                      />
                      {t('Scrooge McDuck Approved')}
                    </span>
                  )}
                </div>

                <div className="w-full text-center shrink-0">
                  <div className="text-sm font-bold arena-heading" style={{ color: TEXT_PRIMARY, textShadow: '0 2px 4px rgba(0,0,0,0.5)' }}>
                    {previewCard.name}
                  </div>
                  {(() => {
                    const f = previewListing.foiling || previewCard.printings?.[0]?.foiling || 'S';
                    return isFoilFinish(f) ? (
                      <span className="text-[0.6875cqw] font-semibold" style={{ color: f === 'R' ? '#c480e0' : '#6ec8d4' }}>
                        {FOIL_LABEL[f]}
                      </span>
                    ) : null;
                  })()}
                </div>

                <div className="w-full flex flex-col gap-1 text-xs shrink-0">
                  <div className="flex justify-between">
                    <span style={{ color: TEXT_MUTED }}>{t('Type')}</span>
                    <span style={{ color: TEXT_BODY }}>{previewCard.type_text || previewCard.type || t('Unknown')}</span>
                  </div>
                  <div className="flex justify-between">
                    <span style={{ color: TEXT_MUTED }}>{t('Rarity')}</span>
                    <span style={{ color: RARITY_COLORS[previewCard.rarity] || TEXT_BODY }}>
                      {previewCard.rarity || t('Unknown')}
                    </span>
                  </div>
                  {previewCard.elements?.length > 0 && (
                    <div className="flex justify-between items-center">
                      <span style={{ color: TEXT_MUTED }}>{t('Elements')}</span>
                      <div className="flex items-center gap-1.5">
                        {previewCard.elements.map((el) => (
                          <span key={el.name} className="flex items-center gap-0.5">
                            <SorceryElementIcon element={el.name} className="size-3.5" />
                            <span style={{ color: TEXT_BODY }}>{el.name}</span>
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                  {previewCard.cost != null && (
                    <div className="flex justify-between">
                      <span style={{ color: TEXT_MUTED }}>{t('Cost')}</span>
                      <span style={{ color: ACCENT_GOLD }}>{previewCard.cost}</span>
                    </div>
                  )}
                </div>

                <OrnamentalDivider className="w-full shrink-0 mt-auto" />

                <div className="w-full flex flex-col gap-1 text-xs shrink-0">
                  <div className="flex justify-between">
                    <span style={{ color: TEXT_MUTED }}>{t('Seller')}</span>
                    <span style={{ color: TEXT_BODY }}>{previewListing.sellerName}</span>
                  </div>
                  {previewListing.createdAt && (
                    <div className="flex justify-between">
                      <span style={{ color: TEXT_MUTED }}>{t('Expires')}</span>
                      <span className="tabular-nums" style={{ color: TEXT_BODY }}>
                        {formatTimeRemaining(previewListing.createdAt, this.state.now)}
                      </span>
                    </div>
                  )}
                  <div className="flex justify-between items-center">
                    <span style={{ color: TEXT_MUTED }}>{t('Price')}</span>
                    <span className="flex items-center gap-1.5">
                      <CoinIcon size={14} />
                      <span className="text-lg font-bold" style={{ color: COIN_COLOR, textShadow: `0 0 10px ${GOLD} 0.3)` }}>
                        {previewListing.price}
                      </span>
                      <span style={{ color: TEXT_MUTED }}>{t('gold')}</span>
                    </span>
                  </div>
                </div>

                <div className="w-full mt-2 shrink-0">
                  {!canAfford ? (
                    <div className="flex flex-col gap-2">
                      <button
                        type="button"
                        disabled
                        className="w-full py-2.5 text-sm font-semibold cursor-not-allowed opacity-40"
                        style={GOLD_BTN}
                      >
                        {t('Buy Now')}
                      </button>
                      <div className="text-center text-[0.6875cqw]" style={{ color: '#c45050' }}>{t('Not enough gold')}</div>
                    </div>
                  ) : (
                    <button
                      type="button"
                      disabled={buyingId === previewListing.id}
                      className="w-full py-2.5 text-sm font-semibold cursor-pointer transition-all"
                      style={GOLD_BTN}
                      onClick={() => this.handleBuy(previewListing)}
                    >
                      {buyingId === previewListing.id ? <span className="relative"><span className="invisible">{t('Buy Now')}</span><span className="absolute inset-0 flex items-center justify-center"><RuneSpinner size={18} /></span></span> : t('Buy Now')}
                    </button>
                  )}
                </div>
              </div>
            ) : null}
          </div>
        </div>
      </div>
    );
  }

  renderSell() {
    const { profile, sorceryCards } = this.props;
    const {
      selectedCardId, sellPrice, sellQuantity, sellLoading, sellError,
      myListings, myListingsLoading, cancellingId,
      sellMatchesCard,
    } = this.state;

    // Listing is decoupled from deck membership: a player may sell any
    // card they own, even if it's slotted in saved decks. Decks are
    // ownership-agnostic (canAddCard does not check ownership), so a
    // sale just leaves the affected decks under-stocked until the
    // player reshuffles them.
    const ownedMap = buildOwnedMap(profile.collection || []);
    const variantOwned = buildVariantOwnedMap(profile.collection || []);
    const { sortBy, sortOrder } = this.state;

    let availableCards = (sorceryCards || []).filter(
      (card) => (ownedMap.get(card.unique_id) || 0) > 0,
    );

    availableCards = availableCards.filter(sellMatchesCard);

    const FOILING_ORDER = { S: 0, F: 1, R: 2 };
    const RARITY_ORDER = { Ordinary: 0, Exceptional: 1, Elite: 2, Unique: 3, Avatar: 4 };
    if (sortBy === 'rarity') {
      availableCards = [...availableCards].sort((a, b) => {
        const diff = (RARITY_ORDER[b.rarity] || 0) - (RARITY_ORDER[a.rarity] || 0);
        return sortOrder === 'asc' ? -diff : diff;
      });
    } else if (sortBy === 'price') {
      availableCards = [...availableCards].sort((a, b) => {
        const diff = (a.name || '').localeCompare(b.name || '');
        return sortOrder === 'asc' ? diff : -diff;
      });
    }

    // Expand each owned card into one tile per (art group x foiling) it is
    // actually owned in. Each tile shows the quantity of that exact variant,
    // so a foil. Or an alt art. Can be listed independently of the copies
    // the player owns in other printings.
    const sellEntries = [];
    for (const card of availableCards) {
      for (const artGroup of artGroupsForCard(card)) {
        const printings = printingsInArtGroup(card, artGroup);
        // Canonical order so standard leads; anything outside the known set
        // trails it, so a future finish still surfaces rather than vanishing.
        const finishes = [...new Set(printings.map((p) => p.foiling || 'S'))]
          .sort((a, b) => (FOILING_ORDER[a] ?? 99) - (FOILING_ORDER[b] ?? 99));
        for (const f of finishes) {
          const qty = getOwnedQty(variantOwned, card.unique_id, artGroup, f);
          if (qty <= 0) continue;
          const printing = printings.find((p) => (p.foiling || 'S') === f);
          if (!printing) continue;
          sellEntries.push({ card, printing, artGroup, foiling: f, qty });
        }
      }
    }

    const { sellMatchesFoiling } = this.state;
    const filteredSellEntries = sellEntries.filter((e) => sellMatchesFoiling(e.foiling));

    const selectedCard = selectedCardId ? findCard(sorceryCards, selectedCardId) : null;
    const selectedFoiling = this.state.selectedFoiling || 'S';
    const selectedArtGroup = this.state.selectedArtGroup || ART_GROUP_BASE;
    const selectedQty = selectedCardId
      ? getOwnedQty(variantOwned, selectedCardId, selectedArtGroup, selectedFoiling)
      : 0;

    const active = myListings.filter((l) => l.status === 'active');
    const sold = myListings.filter((l) => l.status === 'sold');

    return (
      <div className="flex gap-0 flex-1 min-h-0">
        {/* Left column: Your Collection + Sell Panel */}
        <div className="flex-[65] flex flex-col min-h-0 pr-4">
          <CardFilterBar
            className="mb-3"
            sorceryCards={sorceryCards}
            features={{ scope: false, finish: true, type: true }}
            placeholder={t('Search your cards…')}
            onChange={this.handleSellFilterChange}
            trailing={(
              <TabBar className="flex items-center" style={TAB_BAR_STYLE} activeIndex={['rarity', 'name'].indexOf(sortBy)}>
                {[['rarity', t('Rarity')], ['name', t('Name')]].map(([key, label]) => (
                  <button
                    key={key}
                    data-tab-item
                    type="button"
                    className="px-3 py-1.5 text-[0.6875cqw] font-medium cursor-pointer capitalize"
                    style={sortBy === key ? TAB_ACTIVE_TEXT : TAB_INACTIVE}
                    onClick={() => this.handleSort(key)}
                  >
                    {label}{sortIndicator(sortBy, key, sortOrder)}
                  </button>
                ))}
              </TabBar>
            )}
          />

          {/* Card grid. CardGrid owns its scroll + virtualization and renders
              foil printings through the shared WebGL scene. Empty state overlays
              it; the grid stays mounted so the foil context isn't torn down. */}
          <div className="flex-1 flex min-h-0 relative">
            <CardGrid
              ref={this.sellGridRef}
              items={filteredSellEntries}
              getItemKey={this.sellGetItemKey}
              getItemFoil={this.sellGetItemFoil}
              getItemCellStyle={this.sellGetItemCellStyle}
              renderTile={this.sellRenderTile}
              renderFooter={this.sellRenderFooter}
              measureFooter={(
                <>
                  <div className="mt-1" style={{ height: '1.2cqw' }} />
                  <div className="text-[0.625cqw] mt-0.5" style={{ lineHeight: 1.3 }}>&nbsp;</div>
                </>
              )}
            />
            {filteredSellEntries.length === 0 ? (
              <div className="absolute inset-0 flex items-center justify-center text-sm" style={{ color: TEXT_MUTED }}>{t('No available cards to sell')}</div>
            ) : null}
          </div>

          {/* Sell panel at bottom */}
          {selectedCard && (() => {
            const priceInt = parseInt(sellPrice, 10) || 0;
            // Fee preview: the entered price IS the realised sale.
            const { proceeds: netEach } = computeAuctionFee(priceInt, LISTING_FEE_PERCENT);
            const disabled = sellLoading || !sellPrice || priceInt <= 0;
            const isFoil = isFoilFinish(this.state.selectedFoiling);
            const isRainbow = this.state.selectedFoiling === 'R';
            const submitLabel = sellQuantity > 1
              ? t('List {n} Auctions', { n: sellQuantity })
              : t('List Auction');
            const fieldLabelStyle = { color: TEXT_MUTED };
            const fieldLabelClass = 'text-[0.5625cqw] font-semibold';
            const inputStyle = { ...INPUT_STYLE, borderRadius: 'var(--radius-md)', color: TEXT_PRIMARY, height: '2.5cqw' };

            return (
              <>
                <OrnamentalDivider className="my-3" />
                <div
                  className="relative flex items-stretch gap-5 px-5 py-4 shrink-0"
                  style={{ background: 'rgba(12, 10, 8, 0.92)', border: `1px solid ${GOLD} 0.18)`, borderRadius: 'var(--radius-lg)' }}
                >
                  <FourCorners />

                  <img
                    src={cardImageUrl(selectedCard)}
                    alt={selectedCard.name}
                    className="w-20 rounded-card aspect-[63/88] object-cover bg-black/40 shrink-0 self-start"
                  />

                  <div className="flex-1 flex flex-col gap-3 min-w-0">
                    {/* Title row. Card name, foil badge, and the
                        seller meta pinned right. */}
                    <div className="flex items-center gap-3 min-w-0">
                      <span className="text-base font-bold truncate" style={{ color: TEXT_PRIMARY }}>
                        {selectedCard.name}
                      </span>
                      {isFoil && (
                        <span
                          className="flex items-center gap-1 shrink-0 text-[0.625cqw] font-semibold px-1.5 py-0.5 rounded"
                          style={{
                            color: isRainbow ? '#c480e0' : ACCENT_GOLD,
                            background: isRainbow ? 'rgba(196,128,224,0.1)' : `${GOLD} 0.1)`,
                            border: `1px solid ${isRainbow ? 'rgba(196,128,224,0.25)' : `${GOLD} 0.25)`}`,
                          }}
                        >
                          <Sparkles style={{ width: "0.6875cqw", height: "0.6875cqw" }} />
                          {FOIL_LABEL[this.state.selectedFoiling]}
                        </span>
                      )}
                      <div
                        className="ml-auto flex items-center gap-3 shrink-0 text-[0.625cqw]"
                        style={{ color: TEXT_MUTED }}
                      >
                        <span className="flex items-baseline gap-1.5">
                          {t('Available')}
                          <span className="text-sm font-bold tabular-nums normal-case tracking-normal" style={{ color: ACCENT_GOLD }}>
                            {selectedQty}
                          </span>
                        </span>
                        <span className="flex items-baseline gap-1.5">
                          {t('Fee')}
                          <span className="text-sm font-bold tabular-nums normal-case tracking-normal" style={{ color: ACCENT_GOLD }}>
                            {LISTING_FEE_PERCENT}%
                          </span>
                        </span>
                      </div>
                    </div>

                    {/* Form row. Fields on the left, actions pinned to the right */}
                    <div className="flex items-end flex-wrap gap-3 min-w-0">
                      <div className="flex flex-col gap-1.5">
                        <span className={fieldLabelClass} style={fieldLabelStyle}>{t('Qty')}</span>
                        <input
                          type="number"
                          min="1"
                          max={selectedQty}
                          value={sellQuantity}
                          onInput={(e) => this.setState({ sellQuantity: e.target.value })}
                          onBlur={(e) => {
                            const v = Math.max(1, Math.min(parseInt(e.target.value, 10) || 1, selectedQty));
                            this.setState({ sellQuantity: v });
                          }}
                          className="w-[4.5cqw] px-2 text-sm outline-none text-center tabular-nums"
                          style={inputStyle}
                        />
                      </div>

                      <div className="flex flex-col gap-1.5">
                        <span className={fieldLabelClass} style={fieldLabelStyle}>{t('Price')}</span>
                        <input
                          type="number"
                          min="1"
                          placeholder={t('Gold')}
                          value={sellPrice}
                          onInput={(e) => this.setState({ sellPrice: e.target.value })}
                          className="w-32 px-3 text-sm outline-none tabular-nums"
                          style={inputStyle}
                        />
                      </div>

                      <div className="ml-auto flex items-end gap-2">
                        <button
                          type="button"
                          disabled={disabled}
                          className="h-10 px-6 text-xs font-bold cursor-pointer transition-all"
                          style={{ ...GOLD_BTN, opacity: disabled && !sellLoading ? 0.4 : 1 }}
                          onClick={this.handleCreateListing}
                        >
                          {sellLoading ? (
                            <span className="relative">
                              <span className="invisible">{submitLabel}</span>
                              <span className="absolute inset-0 flex items-center justify-center"><RuneSpinner size={16} /></span>
                            </span>
                          ) : submitLabel}
                        </button>
                        <button
                          type="button"
                          className="h-10 px-4 text-xs font-semibold cursor-pointer transition-all"
                          style={{ ...BEVELED_BTN, color: TEXT_BODY, borderRadius: 'var(--radius-md)' }}
                          data-sound={UI.CANCEL}
                          onClick={() => this.setState({
                            selectedCardId: null,
                            selectedArtGroup: ART_GROUP_BASE,
                            selectedFoiling: 'S',
                            sellPrice: '',
                            sellError: null,
                          })}
                        >
                          {t('Cancel')}
                        </button>
                      </div>
                    </div>

                    {/* Net-payout summary. Only after a price is entered, so the
                        row doesn't render an empty placeholder line on first
                        select. */}
                    {priceInt > 0 ? (
                      <div className="text-[0.6875cqw] flex items-baseline gap-1.5" style={{ color: TEXT_MUTED }}>
                        <span>{t('You receive')}</span>
                        <span className="text-sm font-bold tabular-nums" style={{ color: ACCENT_GOLD }}>
                          {netEach.toLocaleString()}
                        </span>
                        <span>{t('gold each')}</span>
                        {sellQuantity > 1 ? (
                          <span>· <span className="tabular-nums" style={{ color: TEXT_BODY }}>{(netEach * sellQuantity).toLocaleString()}</span> {t('total')}</span>
                        ) : null}
                      </div>
                    ) : null}

                    {sellError && (
                      <div className="text-xs" style={{ color: '#c45050' }}>{sellError}</div>
                    )}
                  </div>
                </div>
              </>
            );
          })()}
        </div>

        {/* Right column: Active Listings + Sold */}
        <div className="flex-[35] min-h-0 overflow-y-auto" style={SIDEBAR_PANEL}>
          <div className="relative p-4 h-full" style={{ background: PANEL_BG, border: `1px solid ${GOLD} 0.2)`, borderRadius: 'var(--radius-lg)', isolation: 'isolate' }}>
            <FourCorners />
            <VikingOrnament ornament="ringerike004" variant="centerpiece" opacity={0.08} />

            {myListingsLoading ? (
              <div className="flex flex-col items-center justify-center py-12 gap-3">
                <RuneSpinner size={48} />
                <span className="text-xs" style={{ color: TEXT_MUTED }}>{t('Loading your listings...')}</span>
              </div>
            ) : (
              <div className="flex flex-col gap-5">
                {/* Active listings */}
                <div>
                  <div className="text-[0.625cqw] font-semibold mb-3" style={SECTION_HEADER_STYLE}>
                    {t('Active ({n})', { n: active.length })}
                  </div>
                  <OrnamentalDivider className="mb-3" />
                  {active.length === 0 ? (
                    <div className="text-xs py-4" style={{ color: TEXT_MUTED }}>{t('No active listings')}</div>
                  ) : (
                    <div className="flex flex-col gap-2">
                      {active.map((listing) => {
                        const card = findCard(sorceryCards, listing.cardId);
                        return (
                          <div
                            key={listing.id}
                            data-listing-id={listing.id}
                            className="flex items-center gap-3 px-3 py-2"
                            style={{
                              background: `${GOLD} 0.03)`,
                              border: `1px solid ${GOLD} 0.1)`,
                              borderRadius: 'var(--radius-md)',
                              ...this.listingRowFocusStyle(listing.id),
                            }}
                          >
                            {card && (
                              <img
                                src={cardImageUrl(card)}
                                alt={card.name}
                                className="w-8 rounded-card aspect-[63/88] object-cover bg-black/40 shrink-0"
                              />
                            )}
                            <div className="flex-1 min-w-0">
                              <div className="text-xs font-semibold truncate flex items-center gap-1.5" style={{ color: TEXT_PRIMARY }}>
                                {card?.name || listing.cardId}
                                {isFoilFinish(listing.foiling) && (
                                  <Sparkles className="shrink-0" style={{ width: "0.6875cqw", height: "0.6875cqw", color: listing.foiling === 'R' ? '#c480e0' : ACCENT_GOLD }} />
                                )}
                              </div>
                              {/* Sub-row: how long the listing has left
                                  before it expires. */}
                              <div className="text-[0.5625cqw] tabular-nums mt-0.5" style={{ color: TEXT_MUTED }}>
                                {listing.createdAt ? formatTimeRemaining(listing.createdAt, this.state.now) : ''}
                              </div>
                            </div>
                            <div className="flex flex-col items-end shrink-0 gap-0.5">
                              <span className="text-xs font-bold flex items-center gap-1" style={{ color: COIN_COLOR }}>
                                <CoinIcon size={11} />
                                {listing.price.toLocaleString()}
                              </span>
                            </div>
                            <button
                              type="button"
                              disabled={cancellingId === listing.id}
                              className="px-2 py-1 text-[0.625cqw] font-medium cursor-pointer transition-colors shrink-0 disabled:cursor-not-allowed disabled:opacity-40"
                              style={DANGER_BTN}
                              data-sound={UI.CANCEL}
                              onClick={() => this.handleCancel(listing)}
                            >
                              {cancellingId === listing.id ? <span className="relative"><span className="invisible">{t('Cancel')}</span><span className="absolute inset-0 flex items-center justify-center"><RuneSpinner size={14} /></span></span> : t('Cancel')}
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>

                {/* Sold listings */}
                {sold.length > 0 && (
                  <div>
                    <div className="text-[0.625cqw] font-semibold mb-3" style={SECTION_HEADER_STYLE}>
                      {t('Sold ({n})', { n: sold.length })}
                    </div>
                    <OrnamentalDivider className="mb-3" />
                    <div className="flex flex-col gap-1.5">
                      {sold.slice(0, 5).map((listing) => {
                        const card = findCard(sorceryCards, listing.cardId);
                        const realised = listing.price || 0;
                        return (
                          <div
                            key={listing.id}
                            data-listing-id={listing.id}
                            className="flex items-center gap-3 px-3 py-2"
                            style={{ background: `${GOLD} 0.02)`, borderRadius: 'var(--radius-md)', ...this.listingRowFocusStyle(listing.id) }}
                          >
                            {card && (
                              <img
                                src={cardImageUrl(card)}
                                alt={card.name}
                                className="w-7 rounded-card aspect-[63/88] object-cover bg-black/40 shrink-0"
                              />
                            )}
                            <span className="text-xs flex-1 truncate flex items-center gap-1" style={{ color: TEXT_MUTED }}>
                              {card?.name || listing.cardId}
                              {isFoilFinish(listing.foiling) && (
                                <Sparkles className="shrink-0" style={{ width: "0.625cqw", height: "0.625cqw", color: listing.foiling === 'R' ? '#c480e0' : ACCENT_GOLD }} />
                              )}
                            </span>
                            <span className="text-xs font-semibold shrink-0 flex items-center gap-1" style={{ color: '#6ab04c' }}>
                              +{realised.toLocaleString()}
                              <CoinIcon size={10} />
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  render() {
    const { profile, onBack, onToggleMailbox, mailboxUnreadCount, mailboxDropdown, onToggleFriends, friendListData } = this.props;
    const { tab, error } = this.state;

    return (
      <Fragment>
      <div className="fixed inset-0 z-50 flex flex-col overflow-hidden" style={{ background: '#08080a', color: TEXT_BODY }}>
        {/* Background art + candles share an image-relative coordinate
            system so painted candle positions and flame overlays stay
            locked to the artwork regardless of viewport aspect ratio. */}
        <CoverArtCanvas
          src="/auction-bg.webp"
          aspectRatio={1672 / 941}
          imageStyle={{ filter: 'blur(1.5px)', transform: 'scale(1.008)' }}
          wrapperRef={this.canvasRef}
        />
        {/* Darken overlay */}
        <div className="absolute inset-0 pointer-events-none" style={{ background: 'linear-gradient(180deg, rgba(0,0,0,0.6) 0%, rgba(0,0,0,0.35) 40%, rgba(0,0,0,0.45) 70%, rgba(0,0,0,0.75) 100%)' }} />
        {/* Vignette */}
        <div className="absolute inset-0 pointer-events-none" style={{ background: VIGNETTE }} />

        {/* Torch firelight glow + particles. Behind all UI */}
        <StoreTorchFX />
        <AmbientParticles preset="auction" />

        {/* Candles render in their own coverage canvas AFTER the overlays
            so they sit on top of the darken/vignette/torch layers. */}
        <CoverArtCanvas aspectRatio={1672 / 941}>
          <CandleFlames candles={this.state.candles} />
        </CoverArtCanvas>

        {/* All UI content sits above effects */}
        <div className="relative z-10 flex flex-col flex-1 min-h-0">

        {/* Top bar */}
        <AppHeader
          profile={profile}
          onToggleMailbox={onToggleMailbox}
          mailboxUnreadCount={mailboxUnreadCount}
          mailboxDropdown={mailboxDropdown}
          draftQueueDropdown={this.props.draftQueueDropdown}
          onToggleFriends={onToggleFriends}
          friendListData={friendListData}
          centerContent={
            <TabBar
              className="flex items-center"
              variant="underline"
              barInset="1cqw"
              barStyle={{ background: `linear-gradient(90deg, transparent, ${GOLD} 0.6), transparent)`, boxShadow: 'none' }}
              activeIndex={AUCTION_TABS.findIndex((at) => at.key === tab)}
            >
              {AUCTION_TABS.map((at) => (
                <button
                  key={at.key}
                  type="button"
                  data-tab-item
                  data-tutorial={at.key === 'browse' ? 'auction-tab-browse' : 'auction-tab-sell'}
                  className="px-8 py-2.5 text-xs font-semibold"
                  style={{
                    color: tab === at.key ? '#e8d5a0' : 'rgba(166,160,155,0.5)',
                    textShadow: tab === at.key ? '0 0 10px rgba(180,140,60,0.2)' : 'none',
                  }}
                  onClick={() => this.switchTab(at.key)}
                >
                  <span className="arena-heading">{t(at.label)}</span>
                </button>
              ))}
            </TabBar>
          }
 >
 <BackButton title={t('Back to Hub')} onClick={onBack} />
 <div className="text-sm font-bold arena-heading" style={{ color: TEXT_PRIMARY, textShadow: '0 1px 2px rgba(0,0,0,0.5)' }}>
 {t('Auction House')}
 </div>
 </AppHeader>

 {/* Error banner */}
 {error && (
 <div
 className="relative mx-6 mt-3 px-4 py-2 text-xs shrink-0"
 style={{
 background: 'rgba(180,60,60,0.08)',
 border: '1px solid rgba(180,60,60,0.25)',
 borderRadius: 'var(--radius-md)',
 color: '#c45050',
 }}
 >
 {error}
 <button
 type="button"
 className="ml-2 underline hover:no-underline cursor-pointer"
 onClick={() => this.setState({ error: null })}
 >
 {t('dismiss')}
 </button>
 </div>
 )}

 {/* Content area */}
 <motion.div data-tutorial="auction-content" className="relative flex-1 flex flex-col min-h-0 px-6 py-4" {...screenPanel(1)}>
 {tab === 'browse' && this.renderBrowse()}
 {tab === 'sell' && this.renderSell()}
 </motion.div>

        {/* Purchase toast */}
        {this.state.purchaseMessage && (
          <div
            className="fixed bottom-8 left-1/2 -translate-x-1/2 z-[80] px-5 py-3 rounded-lg text-sm font-medium pointer-events-none"
            style={{
              background: 'rgba(12,10,8,0.95)',
              border: `1px solid ${GOLD} 0.3)`,
              color: ACCENT_GOLD,
              boxShadow: `0 8px 32px rgba(0,0,0,0.5), 0 0 20px ${GOLD} 0.1)`,
            }}
          >
            {this.state.purchaseMessage}
          </div>
        )}
        </div>{/* end UI content wrapper */}
        {this.state.inspectedEntry ? (
          <CardInspector
            card={this.state.inspectedEntry.card}
            imageUrl={this.state.inspectedEntry.printing?.image_url}
            rarity={this.state.inspectedEntry.card?.rarity}
            foiling={this.state.inspectedEntry.printing?.foiling}
            onClose={() => this.setState({ inspectedEntry: null })}
          />
        ) : null}

        {/* First-run auction house tutorial. */}
        {this.state.showAuctionTutorial && (
          <TutorialOverlay
            steps={auctionTutorialSteps()}
            onDismiss={this.handleAuctionTutorialDismiss}
          />
        )}
      </div>
      {/* Dev-only candle editor. Rendered outside the scene wrapper so
          its fixed-positioned UI anchors to the viewport, not the
          scene's containing block. */}
      {isDevBuild() ? (
        <CandlePlacementDev
          sceneKey="auction"
          defaultCandles={AUCTION_DEFAULT_CANDLES}
          onChange={(candles) => this.setState({ candles })}
          getCanvasRect={() => this.canvasRef.current?.getBoundingClientRect()}
        />
      ) : null}
      </Fragment>
    );
  }
}
